import { Typography, Box } from "@mui/material";
import pluralize from "pluralize";
import { useMemo } from "react";

import { TagInfo, TagType } from "../types";
import { RemovedTagChip } from "./RemovedTagChip";
import { TagChip } from "./TagChip";

export const TagList = ({
  selectedTags,
  removedTags,
  onRemove,
  onRestore,
  onPromote,
  theme,
  entityName,
}: {
  selectedTags: TagInfo[];
  removedTags: TagInfo[];
  onRemove: (tagName: string) => void;
  onRestore: (tagName: string) => void;
  onPromote: (tagName: string) => void;
  theme: any;
  entityName: string;
}) => {
  const sortedTags = useMemo(
    () =>
      selectedTags.sort((a, b) => {
        // Sort by type first: NEW, then ALL, then PARTIAL
        const typeOrder = {
          [TagType.NEW]: 0,
          [TagType.ALL]: 1,
          [TagType.PARTIAL]: 2,
        };
        const aOrder = typeOrder[a.type];
        const bOrder = typeOrder[b.type];

        if (aOrder !== bOrder) {
          return aOrder - bOrder;
        }

        // Then by alphabetical
        return a.name.localeCompare(b.name);
      }),
    [selectedTags]
  );
  const sortedRemovedTags = useMemo(
    () => removedTags.sort((a, b) => a.name.localeCompare(b.name)),
    [removedTags]
  );

  if (selectedTags.length === 0 && removedTags.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
        No tags selected. All tags will be removed from selected{" "}
        {pluralize(entityName.toLowerCase())}.
      </Typography>
    );
  }

  return (
    <>
      {/* Regular tags */}
      {sortedTags.map((tagInfo, index) => (
        <TagChip
          key={`${tagInfo.name}-${index}`}
          tagInfo={tagInfo}
          onRemove={onRemove}
          onPromote={onPromote}
          theme={theme}
        />
      ))}

      {/* Removed tags section */}
      {sortedRemovedTags.length > 0 && (
        <>
          <Box sx={{ width: "100%", mt: 1 }}>
            <Typography variant="caption" color="text.secondary" fontWeight="bold">
              Removed:
            </Typography>
          </Box>
          {sortedRemovedTags.map((tagInfo, index) => (
            <RemovedTagChip
              key={`removed-${tagInfo.name}-${index}`}
              tagInfo={tagInfo}
              onRestore={onRestore}
              theme={theme}
            />
          ))}
        </>
      )}
    </>
  );
};
